import type { TFunction } from 'i18next';
import type { Sample } from '../services/sample.service';
import { formatSampleDate, formatSampleDateTime } from './dateFnsLocale';
import { escapeHtml } from './escapeHtml';
import { formatEntityDisplayName, softBreakIdentifier } from './softBreakIdentifier';
import { formatSampleTypeDisplay, formatSampleTypeListDisplay } from './sampleTypeLabels';
import { formatBslLevelCellHtml } from './sampleBslDisplay';
import { getSampleListRowTone } from './sampleUsageRecords';
import { canManageSample } from './samplePermissions';
import { resolveSampleUsageActionUi } from './sampleUsageActionUi';
import {
  formatSampleStorageLabels,
  sampleToCaseLocationCell,
} from './caseLocationUtils';
import { renderSampleStorageLinkHtml } from './sampleStorageLinkHtml';
import { joinLabeledValue } from './joinLabeledValue';
import { formatSampleQuantityWithUnit } from './sampleUnitLabels';
import { formatPathogenGenusDisplay } from './pathogenGenusDisplay';
import { sampleSelectLabels } from './sampleSelectLabels';

/** モバイル facts 行で残量を出さない（切れ／廃棄はチップ・行トーンが担う） */
export function shouldOmitQtyFromMobileFacts(sample: {
  remainingQuantity?: number | null;
}): boolean {
  return sample.remainingQuantity === 0;
}

/** 残量セル HTML（可視は数量のみ・列見出しが「残」。textContent 用に `残 ·` を隠して載せる） */
export function renderSampleQtyCellHtml(row: Sample, t: TFunction): string {
  const val = formatSampleQuantityWithUnit(row.remainingQuantity, row.unit, t);
  const label = t('sample.remaining_quantity_short', {
    defaultValue: t('sample.remaining_quantity'),
  });
  const labeled = joinLabeledValue(label, val);
  const labelHtml = `<span class="sample-qty-label visually-hidden" aria-hidden="true">${escapeHtml(`${label} · `)}</span>`;
  const tone = getSampleListRowTone(row);
  const toneClass =
    tone === 'disposed' ? ' sample-qty--disposed' : tone === 'depleted' ? ' sample-qty--zero' : '';

  if (tone === 'disposed') {
    const chip = escapeHtml(
      t('sample_filter.status_disposed', {
        defaultValue: t('sample.stats_disposed', { defaultValue: 'Disposed' }),
      })
    );
    return `<span class="sample-qty-value${toneClass} sample-qty--disposed-chip" title="${escapeHtml(labeled)}">${labelHtml}<span class="sample-qty-num">${escapeHtml(val)}</span><span class="sample-qty-chip">${chip}</span></span>`;
  }

  if (shouldOmitQtyFromMobileFacts(row)) {
    return `<span class="sample-qty-value${toneClass} sample-qty--omit-mobile" title="${escapeHtml(labeled)}">${labelHtml}${escapeHtml(val)}</span>`;
  }

  return `<span class="sample-qty-value${toneClass}" title="${escapeHtml(labeled)}">${labelHtml}${escapeHtml(val)}</span>`;
}

/** @deprecated テスト互換: 残量セルを facts 相当として返す */
export function renderSampleMobileFactsHtml(
  row: Sample,
  t: TFunction,
  _language?: string
): string {
  void _language;
  const qty = renderSampleQtyCellHtml(row, t);
  return `<span class="sample-mobile-facts">${qty}</span>`;
}

export type SampleManagerColumnDeps = {
  t: TFunction;
  language: string;
  userId: number | undefined;
  isAdmin: boolean;
  adminViewAll: boolean;
  selectedSampleIds: Set<number>;
};

/** DataTables stateSave キー接頭辞（列既定・初期ソートの変更時は bump） */
export const SAMPLE_MANAGER_DT_STATE_PREFIX = 'DataTables_sample-manager_v17_';

/**
 * buildSampleManagerDataTableColumns の列 index。
 * stateLoad / 初期 order / ColVis 除外と同期すること。
 */
export const SAMPLE_MANAGER_REGISTRANT_COLUMN_INDEX = 1;
export const SAMPLE_MANAGER_CREATED_AT_COLUMN_INDEX = 11;
export const SAMPLE_MANAGER_LAST_USED_COLUMN_INDEX = 12;

/** ColVis 対象: チェック・操作以外（作製日は名前インラインのため既定非表示・切替可） */
export const SAMPLE_MANAGER_COLVIS_COLUMNS = ':not(.noVis)';

type SampleRowActionDeps = Pick<
  SampleManagerColumnDeps,
  't' | 'userId' | 'isAdmin' | 'adminViewAll'
>;

/** 行操作: 使用は常に1タップ（不可時は状態チップ）。閲覧/編集/削除は1組（md+ 横並び、未満は ⋯） */
export function renderSampleRowActionsHtml(row: Sample, deps: SampleRowActionDeps): string {
  const { t, userId, isAdmin, adminViewAll } = deps;
  const canManage = canManageSample(row, userId, { isAdmin, adminViewAll });
  const readOnlyTitle = t('sample.case_manager.read_only_other_user');
  const usageUi = resolveSampleUsageActionUi(row, { canManage, t });
  const btnClass = 'btn btn-sm sample-row-action-btn border-0 bg-transparent';
  const manageDisabled = !canManage
    ? `disabled title="${escapeHtml(readOnlyTitle)}"`
    : '';
  const iconAction = (
    extraClass: string,
    attrs: string,
    ariaLabel: string,
    icon: string,
    label: string
  ) =>
    `<button type="button" class="${btnClass} ${extraClass}" data-id="${row.id}" ${attrs} aria-label="${ariaLabel}"><i class="fas ${icon}" aria-hidden="true"></i><span class="sample-action-menu-label" aria-hidden="true">${label}</span></button>`;

  const usageShort = escapeHtml(
    t('sample.add_usage_record_short', { defaultValue: t('sample.add_usage_record') })
  );
  const viewFull = escapeHtml(t('sample.open_detail_page'));
  const viewShort = escapeHtml(
    t('sample.open_detail_short', { defaultValue: t('sample.open_detail_page_short') })
  );
  const editLabel = escapeHtml(t('common.edit'));
  const deleteLabel = escapeHtml(t('common.delete'));
  const displayName = formatEntityDisplayName(row.name || String(row.id), t);
  const moreAria = escapeHtml(
    t('sample.row_actions_aria', {
      name: displayName,
      defaultValue: `${t('common.actions')} · ${displayName}`,
    })
  );
  const moreTitle = escapeHtml(
    t('sample.row_more_actions_title', {
      name: displayName,
      defaultValue: t('common.more_actions'),
    })
  );
  const usageSlot =
    usageUi.kind === 'button'
      ? iconAction(
          'usage-record-btn sample-row-usage-inline text-primary',
          `title="${escapeHtml(usageUi.title)}"`,
          usageShort,
          'fa-notes-medical',
          usageShort
        )
      : `<span class="sample-row-usage-blocked sample-row-usage-inline" role="status" title="${escapeHtml(usageUi.title)}" aria-label="${escapeHtml(usageUi.shortLabel)}">${escapeHtml(usageUi.shortLabel)}</span>`;
  const viewBtn = iconAction(
    'view-btn text-secondary',
    `title="${viewFull}"`,
    viewShort,
    'fa-eye',
    viewShort
  );
  const editBtn = iconAction(
    'edit-btn text-primary',
    manageDisabled || `title="${escapeHtml(t('sample.edit_sample_description'))}"`,
    editLabel,
    'fa-pen',
    editLabel
  );
  const deleteBtn = iconAction(
    'delete-btn text-danger',
    manageDisabled || `title="${escapeHtml(t('sample.delete_sample_description'))}"`,
    deleteLabel,
    'fa-trash-alt',
    deleteLabel
  );

  // manage は details 内（閉じ時に a11y ツリーへ出さない）。md+ は CSS で横並び展開。
  return `
    <div class="sample-row-actions" role="group" aria-label="${escapeHtml(t('common.actions'))}">
      ${usageSlot}<span class="visually-hidden" aria-hidden="true"> · </span>
      <details class="sample-row-more">
        <summary class="${btnClass} sample-row-more-summary" title="${moreTitle}" aria-label="${moreAria}">
          <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
        </summary>
        <div class="sample-row-manage">
          ${viewBtn}<span class="visually-hidden" aria-hidden="true"> · </span>${editBtn}<span class="visually-hidden" aria-hidden="true"> · </span>${deleteBtn}
        </div>
      </details>
    </div>
  `;
}

/** SampleManager DataTables の columns 定義（ページ本体から分離） */
export function buildSampleManagerDataTableColumns(deps: SampleManagerColumnDeps) {
  const { t, language, userId, isAdmin, adminViewAll, selectedSampleIds } = deps;

  return [
    {
      data: null,
      title: `<input type="checkbox" id="selectAllCheckbox" name="selectAllCheckbox" aria-label="${escapeHtml(t('sample.select_all'))}" title="${escapeHtml(t('sample.select_all_title', { defaultValue: t('sample.select_all') }))}" />`,
      className: 'text-center noVis sample-check-col',
      orderable: false,
      width: '1%',
      render: (_data: unknown, _type: unknown, row: Sample) => {
        const isChecked = selectedSampleIds.has(row.id) ? 'checked' : '';
        const displayName = formatEntityDisplayName(row.name || String(row.id), t);
        const { ariaLabel, title } = sampleSelectLabels(displayName, t);
        const nameLabel = escapeHtml(ariaLabel);
        const selectTitle = escapeHtml(title);
        return `<label class="sample-check-hit" title="${selectTitle}"><input type="checkbox" id="sample-checkbox-${row.id}" name="sample-checkbox-${row.id}" class="sample-checkbox" data-id="${row.id}" ${isChecked} aria-label="${nameLabel}" title="${selectTitle}" /></label>`;
      },
    },
    {
      data: 'creatorDisplayName',
      title: `<span title="${escapeHtml(
        t('sample.maker_name', { defaultValue: '作製者名' })
      )}">${escapeHtml(
        t('sample.registrant_name', { defaultValue: '作製者' })
      )}</span>`,
      defaultContent: '-',
      width: '9%',
      className: 'text-nowrap sample-hide-mobile sample-meta-col sample-creator-col',
      /** 全保管庫表示時のみ初期表示。自分のみのときは氏名が全行同一で冗長（列切替で復活可） */
      visible: isAdmin && adminViewAll,
      render: (data: unknown) => {
        if (data == null || data === '') return '-';
        const name = String(data);
        return `<span class="sample-creator-cell" title="${escapeHtml(name)}">${escapeHtml(name)}</span>`;
      },
    },
    {
      data: 'name',
      title: t('sample.sample_name'),
      width: '28%',
      className: 'sample-name-col',
      /** 省略は CSS。タップで詳細。作製日インラインはモバイルで名前直下（専用列は sm+） */
      render: (data: unknown, type: string, row: Sample) => {
        const raw = data == null || data === '' ? '-' : String(data);
        if (type === 'sort' || type === 'type' || type === 'filter') {
          return raw;
        }
        const display = formatEntityDisplayName(raw, t);
        const full = escapeHtml(display);
        const softLabel = escapeHtml(softBreakIdentifier(raw, t));
        const openFull = escapeHtml(t('sample.open_quick_detail'));
        let createdInline = '';
        if (row.createdAt != null && row.createdAt !== '') {
          try {
            const fullDt = formatSampleDateTime(row.createdAt, language);
            const day = formatSampleDate(row.createdAt, language);
            createdInline = `<span class="sample-name-created" title="${escapeHtml(fullDt)}" aria-hidden="true"> · ${escapeHtml(day)}</span>`;
          } catch {
            /* 日付パース失敗時はインライン作製日を省略（専用列にフォールバック） */
          }
        }
        // 可視＝名前。aria も名前（操作説明は title）
        return `<button type="button" class="sample-name-text sample-name-open" data-id="${row.id}" title="${openFull} · ${full}" aria-label="${full}"><span class="sample-name-label">${softLabel}</span>${createdInline}</button>`;
      },
    },
    {
      data: 'sampleType',
      title: t('sample.sample_type'),
      defaultContent: '-',
      width: '5%',
      className: 'sample-type-cell sample-hide-mobile sample-meta-col',
      render: (data: unknown) => {
        const raw =
          typeof data === 'string' ? data : data != null && data !== '' ? String(data) : '';
        const full = formatSampleTypeDisplay(raw, t);
        const display = formatSampleTypeListDisplay(raw, t);
        const fullEscaped = escapeHtml(full);
        const shortEscaped = escapeHtml(display);
        return `<span class="sample-type-label" title="${fullEscaped}">${shortEscaped}</span>`;
      },
    },
    {
      data: 'bslLevel',
      title: `<span title="${escapeHtml(t('sample.bsl_level'))}">${escapeHtml(
        t('sample.bsl_level_short', { defaultValue: 'BSL' })
      )}</span>`,
      defaultContent: '-',
      width: '1%',
      className: 'text-nowrap sample-hide-mobile sample-meta-col sample-bsl-col',
      render: (data: unknown) => formatBslLevelCellHtml(data),
    },
    {
      data: 'remainingQuantity',
      title: `<span title="${escapeHtml(t('sample.remaining_quantity'))}">${escapeHtml(
        t('sample.remaining_quantity_short', { defaultValue: 'Rem' })
      )}</span>`,
      className: 'text-end sample-qty-col',
      defaultContent: '-',
      width: '1%',
      render: (_data: unknown, _type: unknown, row: Sample) => renderSampleQtyCellHtml(row, t),
    },
    {
      data: 'storageRoom',
      title: `<span title="${escapeHtml(t('sample.storage_room'))}">${escapeHtml(
        t('sample.storage_room_short', { defaultValue: t('sample.storage_room') })
      )}</span>`,
      defaultContent: '-',
      width: '26%',
      className: 'sample-storage-col',
      type: 'natural-ja',
      render: (_data: unknown, type: string, row: Sample) => {
        const cell = sampleToCaseLocationCell(row);
        const compact = formatSampleStorageLabels(cell, row.storageRoom ?? null, t).compact;
        if (type === 'sort' || type === 'type' || type === 'filter') {
          return compact;
        }
        return renderSampleStorageLinkHtml(row, t);
      },
    },
    ...(
      [
        ['pathogenGenus', 'sample.genus'],
        ['pathogenSpecies', 'sample.species'],
        ['pathogenSubspecies', 'sample.subspecies'],
        ['pathogenStrain', 'sample.strain'],
      ] as const
    ).map(([data, titleKey]) => ({
      data,
      title: t(titleKey),
      defaultContent: '-',
      width: '8%',
      className: 'text-nowrap sample-hide-mobile sample-meta-col',
      /** サンプル名に病原体情報が含まれることが多く、横幅確保のため既定非表示 */
      visible: false,
      render: (value: unknown) => {
        if (value == null || value === '') return '-';
        const text =
          data === 'pathogenGenus'
            ? formatPathogenGenusDisplay(String(value))
            : String(value).trim();
        return text ? escapeHtml(text) : '-';
      },
    })),
    {
      data: 'createdAt',
      title: t('sample.created_at'),
      width: '7%',
      className: 'text-nowrap sample-created-col sample-meta-col',
      /** 名前セルに作製日インラインあり。専用列は ColVis で再表示可 */
      visible: false,
      render: (data: unknown) => {
        if (data == null || data === '') return '-';
        try {
          const full = formatSampleDateTime(data as string | number | Date, language);
          const day = formatSampleDate(data as string | number | Date, language);
          return `<span class="sample-created-value" title="${escapeHtml(full)}">${escapeHtml(day)}</span>`;
        } catch {
          return String(data);
        }
      },
    },
    {
      data: (row: Sample) => row.lastUsedDate ?? row.last_used_date ?? null,
      title: t('sample.usage_datetime', { defaultValue: 'Usage date/time' }),
      width: '8rem',
      className: 'text-nowrap sample-hide-mobile sample-meta-col sample-last-used-col',
      defaultContent: '',
      /** 一覧の主ソート軸。狭い画面は sample-hide-mobile で隠す */
      visible: true,
      render: (data: unknown, type: string) => {
        if (type === 'sort' || type === 'type') {
          if (data == null || data === '') return 0;
          const t = new Date(data as string | number | Date).getTime();
          return Number.isNaN(t) ? 0 : t;
        }
        if (data == null || data === '') return '';
        try {
          return formatSampleDateTime(data as string | number | Date, language);
        } catch {
          return String(data);
        }
      },
    },
    {
      data: 'notes',
      title: t('sample.notes'),
      defaultContent: '-',
      width: '8%',
      className: 'text-nowrap notes-cell sample-hide-mobile sample-meta-col',
      visible: false,
      render: (data: unknown) => {
        const escaped = escapeHtml(data ?? '-');
        return `<span class="d-inline-block text-truncate" style="max-width:10rem" title="${escaped}">${escaped}</span>`;
      },
    },
    {
      data: null,
      title: `<span class="visually-hidden">${escapeHtml(t('common.actions'))}</span>`,
      className: 'text-center noVis sample-actions-col',
      orderable: false,
      defaultContent: '',
      width: '1%',
      render: (_data: unknown, _type: unknown, row: Sample) =>
        renderSampleRowActionsHtml(row, { t, userId, isAdmin, adminViewAll }),
    },
  ];
}
